import React from 'react';
import { MessageCircleQuestion } from 'lucide-react';
import { Button, Modal, RobbieMark } from '../ui';
import { prioritiesFromBackend } from '../../utils/sourcingCriteria';

/**
 * "Why these suppliers?" explainer: states, in Robbie's voice, exactly how
 * the ranking was produced — the buyer's own Sourcing Criteria inputs and
 * where the per-supplier reasoning lives. Honest-UI rule: every section
 * renders only from stored requirements; nothing is invented to fill the
 * layout, and the criteria sections disappear when the data is absent.
 */
interface RankingExplainerModalProps {
  open: boolean;
  onClose: () => void;
  /** The RFQ's stored requirements (Sourcing Criteria step). */
  requirements?: {
    tradeoffPriorities?: unknown[];
    desiredLeadTime?: string;
    complianceRequirements?: string[];
    deliveryLocation?: { formattedAddress?: string };
    sourcingLocation?: string;
  };
  /** Hands the question to the docked Robbie panel for a live follow-up. */
  onAskRobbie?: () => void;
}

const RankingExplainerModal: React.FC<RankingExplainerModalProps> = ({
  open,
  onClose,
  requirements,
  onAskRobbie,
}) => {
  const hasPriorities =
    Array.isArray(requirements?.tradeoffPriorities) && requirements!.tradeoffPriorities!.length > 0;
  const priorities = hasPriorities ? prioritiesFromBackend(requirements!.tradeoffPriorities) : [];
  const compliance = Array.isArray(requirements?.complianceRequirements)
    ? requirements!.complianceRequirements!
    : [];
  const facts = [
    requirements?.desiredLeadTime ? { label: 'Lead time', value: requirements.desiredLeadTime } : null,
    requirements?.deliveryLocation?.formattedAddress
      ? { label: 'Delivery to', value: requirements.deliveryLocation.formattedAddress }
      : null,
    requirements?.sourcingLocation
      ? { label: 'Sourced from', value: requirements.sourcingLocation }
      : null,
    compliance.length > 0 ? { label: 'Compliance', value: compliance.join(', ') } : null,
  ].filter((f): f is { label: string; value: string } => f !== null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Why these suppliers?"
      size="md"
      footer={
        <>
          {onAskRobbie && (
            <Button
              variant="secondary"
              iconLeft={<MessageCircleQuestion />}
              onClick={() => {
                onAskRobbie();
                onClose();
              }}
            >
              Ask me a follow-up
            </Button>
          )}
          <Button onClick={onClose}>Got it</Button>
        </>
      }
    >
      <div className="flex flex-col gap-s5 font-sans">
        <div className="flex items-start gap-s3 rounded-control border border-robbie-border-soft bg-ai-chip px-s4 py-s3">
          <RobbieMark size="xs" aria-label="Explained by Robbie" />
          <p className="m-0 text-body-sm text-ink-body">
            I researched suppliers for every component and ranked each list against what you set in
            Sourcing Criteria. My top two per component appear as picks, with my reasoning for that
            specific supplier on its card.
          </p>
        </div>

        {hasPriorities && (
          <div>
            <p className="m-0 mb-s2 text-meta font-semibold text-ink-secondary">
              Your priority order — I weight suppliers in this order
            </p>
            <ol className="m-0 flex list-none flex-col gap-s1 p-0">
              {priorities.map((p, idx) => (
                <li key={p.id} className="flex items-baseline gap-s3 text-body-sm text-ink">
                  <span className="w-4 shrink-0 text-right font-mono text-mono-data text-ink-muted">{idx + 1}</span>
                  <span className="font-semibold">{p.name}</span>
                  <span className="text-ink-muted">{p.description}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {facts.length > 0 && (
          <div>
            <p className="m-0 mb-s2 text-meta font-semibold text-ink-secondary">Also held against every supplier</p>
            <ul className="m-0 flex list-none flex-col gap-s1 p-0">
              {facts.map((f) => (
                <li key={f.label} className="flex items-baseline gap-s3 text-body-sm text-ink">
                  <span className="w-[86px] shrink-0 text-meta text-ink-muted">{f.label}</span>
                  <span>{f.value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="m-0 text-body-sm text-ink-secondary">
          When my research can&rsquo;t justify a ranking, I don&rsquo;t make one up — those suppliers stay
          under &ldquo;Other suppliers&rdquo; without a rank. Shortlisting is always your call: nothing I
          pick moves forward until you shortlist it.
        </p>
      </div>
    </Modal>
  );
};

export default RankingExplainerModal;
